import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import LogisticsCompany from "@/enum/LogisticsCompany";
import { post } from "../http/platformServer";
import { PremiumFunctionType } from "../PremiumValidator";
import { tabId } from "../http/tabId";
import { uuid } from "@/utils";
import { PrintShippingSource } from "./ewaybillGenerate";

/**发货方式 */
export enum ShippingWay {
    OFFLINE = "OFFLINE",
    ONLINE = "ONLINE",
    VIRTUAL = "VIRTUAL",
}

/**订单提交发货电子面单参数 */
export interface ShipOrderEwaybillDTO
    extends Pick<EwaybillDTO, "logisticsCompany" | "parentWaybillNo" | "waybillNo" | "orderItemsInfo" | "type"> {}

export interface ShipCombineItemInfoParams {
    itemName: string;
    productId: number;
    skuId: number;
    quantity: number;
}

/**订单提交发货orderItem参数 */
export interface ShipOrderItemDTO
    extends Pick<OrderItemDTO, "originalId" | "productOriginalId" | "quantity" | "skuOriginalId" | "weight"> {
    /** 识别码，如手机串号 */
    imeis?: string[];

    /** 手机IMEI码2 */
    imei2s?: string[];

    /** 机器码，如电脑 */
    deviceSns?: string[];

    /** EAN码/69码 */
    eanCodes?: string[];

    /** 勾选的待发货组合品的数量与orderItem的组合品数量不一致时，需要传combineItemInfos,无勾选的待发货组合品不需要传 */
    combineItemInfos?: ShipCombineItemInfoParams[];
}

/**发货订单的参数 */
export interface ShipOrderDTO
    extends Pick<OrderDTO, "id" | "logisticsCompany" | "mergeSplitStatus" | "originalId" | "shopId" | "status"> {
    /**
     * @deprecated 是否部分发货 已失效，后面会移除
     */
    isPartShip?: boolean;

    /** 所有OrderItem是否被选中 */
    isAllOrderItemSelected: boolean;

    /** 所有待发货OrderItem是否被选中 */
    isAllPendingShipmentOrderItemSelected: boolean;

    ewaybills: ShipOrderEwaybillDTO[];

    orderItems: ShipOrderItemDTO[];

    waybillNo: string;

    /**
     * 订单关联的电子面单数量
     */
    ewaybillCount: number;
}

/**
 * 发货场景
 */
export enum ShippingScene {
    /** 打单后自动发货（订单列表——打印并发货） */
    AUTO_SHIP_AFTER_PRINTING = "AUTO_SHIP_AFTER_PRINTING",
    /** 拿货标签打印后自动发货 */
    AUTO_SHIP_AFTER_PURCHASING_LABEL_PRINTED = "AUTO_SHIP_AFTER_PURCHASING_LABEL_PRINTED",
    /** 扫描打印后自动发货 */
    AUTO_SHIP_AFTER_SCAN_PRINTING = "AUTO_SHIP_AFTER_SCAN_PRINTING",
    /** 打单后手动发货（订单列表） */
    MANUAL_SHIP_AFTER_PRINTING = "MANUAL_SHIP_AFTER_PRINTING",
    /** 拿货标签打印后手工发货 */
    MANUAL_SHIP_AFTER_PURCHASING_LABEL_PRINTED = "MANUAL_SHIP_AFTER_PURCHASING_LABEL_PRINTED",
    /** 扫描打印后手动发货 */
    MANUAL_SHIP_AFTER_SCAN_PRINTING = "MANUAL_SHIP_AFTER_SCAN_PRINTING",
}

/**提交发货任务参数 */
export interface OrderShipParams {
    orders: ShipOrderDTO[];

    shippingWay: ShippingWay;

    invocationSource?: PrintShippingSource;

    shippingScene?: ShippingScene;

    /**
     * 前端根据当前页面生成一个id，防止多页面操作
     */
    tabId?: string;
    /**
     * 请求id 每次请求都需要生成
     */
    reqId?: string;
}

export interface OrderShipErrorWaybillNoInfo {
    waybillNo: string;
    logisticsCompany: LogisticsCompany;
}

export interface OrderShipError {
    buyerNickname: string;
    buyerUsername: string;
    message: string;
    orderId: number;
    orderOriginalId: string;
    waybillNoInfos: OrderShipErrorWaybillNoInfo[];
}

export interface OrderShipResult {
    errors: OrderShipError[];
    failureCount: number;
    successCount: number;
}

/**
 * 同步批量发货接口 一次最多10个订单
 */
export function syncShip(shopId: number, orderShipParams: OrderShipParams) {
    let path = "/order/ship";

    if (orderShipParams.invocationSource === PrintShippingSource.SCAN_PRINT) {
        path = "/scan_printing/order/ship";
    } else if (orderShipParams.invocationSource === PrintShippingSource.PURCHASING_LABEL) {
        path = "/purchasing_label/order/ship";
    }

    orderShipParams.tabId = tabId;
    orderShipParams.reqId = uuid();

    return post<OrderShipResult>(shopId, path, orderShipParams);
}

/**
 * 同步批量重新发货接口 一次最多10个订单
 */
export function syncReship(shopId: number, params: OrderReshipParams) {
    return post<OrderShipResult>(shopId, "/order/reship", params);
}

export interface OrderReshipParams {
    orders: ShipOrderDTO[];
}

/**
 * 批量预发货
 */
export function preShip(shopId: number, orderIds: number[]) {
    return post<void>(
        shopId,
        "/order/pre_shipping",
        {
            shopId,
            orderIds,
        },
        {
            pathPremiumValidator() {
                return {
                    shopId,
                    type: PremiumFunctionType.PRE_SHIPPING,
                };
            },
        },
    );
}

/**
 * 批量取消预发货
 */
export function cancelPreShip(shopId: number, orderIds: number[]) {
    return post(shopId, "/order/cancel_pre_shipping", {
        shopId,
        orderIds,
    });
}

export interface LogisticsReissueInfo {
    waybillNo: string;
    logisticsCompany: LogisticsCompany;
}
export interface LogisticsReissue {
    orderId: number;
    infos: LogisticsReissueInfo[];
}

export interface ReissueLogisticResultError {
    orderId: number;
    orderOriginalId: string;
    logisticsCompany: LogisticsCompany;
    waybillNo: string;
    buyerNickname: string;
    buyerUsername: string;
    message: string;

    /** 前端值，出现网请求异常等情况使用 */
    waybills?: {
        waybillNo: string;

        logisticsCompany: LogisticsCompany;
    }[];
}

export interface BatchLogisticsReissueResult {
    successCount: number;
    failureCount: number;
    errors: ReissueLogisticResultError[];
}

export function reissueLogistics(shopId: number, logisticsReissues: LogisticsReissue[]) {
    return post<BatchLogisticsReissueResult>(shopId, "/order/upload_extra_waybill_nos", {
        shopId,
        logisticsReissues,
    });
}
